import { useMemo, useCallback } from "react";
import { useOrganizations } from "./useOrganizations";
import { useReviewOrganizations } from "./useReviewSchedule";
import { usePlan } from "../lib/plan";

// One client list, assembled from two services.
//
// The scanner's `organization` table is the source of truth and always has been: it is
// what a scan attaches to, and it is the only place a new organization is created. The
// Business Review Service adds a profile row alongside it — relationship, industry,
// size, revenue, health, next review — which is why `br_organization_profile` is keyed
// on the scanner's own id rather than carrying a name of its own.
//
// So s1 is the spine and s4 is enrichment, NOT an equal partner. Organizations is the
// landing screen; if it went blank every time the Business Review Service was
// unreachable, an optional area would be taking down the front door. A failed s4 fetch
// therefore drops `reviewsAvailable` and the screen renders its base columns, rather
// than surfacing an error.
export function useOrgDirectory() {
  const base = useOrganizations();
  // Business Reviews is a plan feature. Without it the review side is never requested,
  // and the screen renders its base columns with no "unavailable" banner — the columns
  // are not missing, they are not part of the plan.
  const review = useReviewOrganizations(usePlan().can("BUSINESS_REVIEWS"));

  // The review side's `search` param is deliberately never set — see `filter` below.
  // Everything else round-trips, because the KPI and tab counts are computed server-side
  // over the filtered set (routes/organizations.js) and duplicating that math here would
  // give two answers to the same question.
  const setFilter = useCallback(
    (patch) => review.setFilters((f) => ({ ...f, ...patch })),
    [review]
  );

  const reviewsAvailable = !review.error && !review.locked;

  const organizations = useMemo(() => {
    const profiles = new Map(review.organizations.map((o) => [o.id, o]));

    // Base fields win on collision. Both sides carry `name`, and s1's is the one that
    // gets edited.
    let rows = base.organizations.map((org) => ({ ...(profiles.get(org.id) || {}), ...org }));

    // A relationship/health/status filter is answered by s4, so when one is active the
    // rows it did not return are filtered out. Guarded on reviewsAvailable: with the
    // service down the map is empty, and intersecting against it would empty the table.
    const narrowed = review.filters.relationship || review.filters.health || review.filters.status;
    if (reviewsAvailable && narrowed) {
      rows = rows.filter((o) => profiles.has(o.id));
    }
    return rows;
  }, [base.organizations, review.organizations, review.filters, reviewsAvailable]);

  // Saving a profile touches the review side only, but the row on screen is a merge of
  // both — reload the base list too so nothing renders half-stale.
  const saveProfile = useCallback(
    async (id, patch) => {
      const ok = await review.saveProfile(id, patch);
      if (ok) base.reload();
      return ok;
    },
    [review, base]
  );

  return {
    organizations,
    // Only s1 failing is worth blocking the page for.
    error: base.error,
    loading: base.loading,
    refreshing: base.refreshing,
    reload: base.reload,
    create: base.create,

    reviewsAvailable,
    reviewError: review.error,
    kpis: review.kpis,
    counts: review.counts,
    filters: review.filters,
    setFilter,
    saveProfile,
  };
}
